import React, { useEffect, useState } from "react";

const NODE_API = "http://localhost:9090/api";

export default function TeacherQuestions() {
  const [tests, setTests] = useState<any[]>([]);
  const [selectedTestId, setSelectedTestId] = useState<string>("");
  const [questions, setQuestions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Manual Question Form State
  const [form, setForm] = useState({
    questionText: "",
    optionA: "",
    optionB: "",
    optionC: "",
    optionD: "",
    correctOption: "A",
    explanation: ""
  });

  // Bulk CSV Upload State
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadTests();
  }, []);

  useEffect(() => {
    if (selectedTestId) {
      loadTestQuestions(selectedTestId);
    } else {
      setQuestions([]);
    }
  }, [selectedTestId]);

  const loadTests = async () => {
    try {
      const res = await fetch(`${NODE_API}/tests`);
      if (res.ok) {
        const data = await res.json();
        setTests(Array.isArray(data) ? data : []);
        if (data.length > 0) setSelectedTestId(String(data[0].id));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const loadTestQuestions = async (testId: string) => {
    setLoading(true);
    try {
      const res = await fetch(`${NODE_API}/tests/${testId}/questions`);
      if (res.ok) {
        const data = await res.json();
        setQuestions(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddQuestion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTestId) {
      alert("Please select a test first!");
      return;
    }

    try {
      const res = await fetch(`${NODE_API}/tests/${selectedTestId}/question`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to add question");

      alert("Question added successfully!");
      setForm({ questionText: "", optionA: "", optionB: "", optionC: "", optionD: "", correctOption: "A", explanation: "" });
      loadTestQuestions(selectedTestId);
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleBulkUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTestId || !csvFile) {
      alert("Please select a test and choose a CSV file.");
      return;
    }

    setUploading(true);
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const text = event.target?.result as string;
        const lines = text.split("\n").filter((l) => l.trim().length > 0);
        
        let count = 0;
        // CSV Format: questionText, optionA, optionB, optionC, optionD, correctOption(A/B/C/D), explanation
        for (let i = 1; i < lines.length; i++) { // Skip header line
          const cols = lines[i].split(",").map((c) => c.trim().replace(/^"|"$/g, ''));
          if (cols.length >= 6) {
            await fetch(`${NODE_API}/tests/${selectedTestId}/question`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                questionText: cols[0],
                optionA: cols[1],
                optionB: cols[2],
                optionC: cols[3],
                optionD: cols[4],
                correctOption: cols[5],
                explanation: cols[6] || "",
              }),
            });
            count++;
          }
        }
        alert(`Successfully imported ${count} questions via CSV!`);
        setCsvFile(null);
        loadTestQuestions(selectedTestId);
      } catch (err) {
        alert("Failed to parse or upload CSV file.");
      } finally {
        setUploading(false);
      }
    };
    reader.readAsText(csvFile);
  };

  const handleDelete = async (qId: number) => {
    if (!window.confirm("Are you sure you want to delete this question?")) return;
    try {
      const res = await fetch(`${NODE_API}/tests/question/${qId}`, { method: "DELETE" });
      if (res.ok) {
        alert("Question deleted successfully!");
        loadTestQuestions(selectedTestId);
      }
    } catch (err) {
      alert("Failed to delete question");
    }
  };

  return (
    <div className="space-y-6 max-w-6xl font-['Inter']">
      <header className="rounded-[30px] border border-slate-200 bg-white p-6 shadow-sm">
        <p className="text-xs font-black uppercase tracking-wide text-blue-600">Question Bank & Manager</p>
        <h2 className="mt-2 text-3xl font-black text-slate-950">Manage Test Questions</h2>
        <p className="mt-2 text-sm font-bold text-slate-500">
          Select a test from below to view, add manually, or bulk upload via CSV.
        </p>
      </header>

      {/* Test Selector Dropdown Bar */}
      <div className="rounded-[25px] border border-slate-200 bg-white p-5 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="w-full sm:w-auto flex-1">
          <label className="block text-xs font-black uppercase tracking-wide text-slate-400 mb-1">Select Active Test</label>
          <select
            value={selectedTestId}
            onChange={(e) => setSelectedTestId(e.target.value)}
            className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold outline-none focus:border-blue-500"
          >
            <option value="">-- Choose Test --</option>
            {tests.map((t) => (
              <option key={t.id} value={t.id}>
                {t.title} ({t.batchName || 'General'}) - ID: #{t.id}
              </option>
            ))}
          </select>
        </div>
        <div className="text-right">
          <span className="text-xs font-black text-slate-400 uppercase block">Total Questions</span>
          <span className="text-2xl font-black text-blue-600">{questions.length}</span>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[420px_1fr]">
        {/* LEFT COLUMN: ADD QUESTION (MANUAL + BULK CSV) */}
        <div className="space-y-6">
          {/* MANUAL FORM */}
          <form onSubmit={handleAddQuestion} className="rounded-[30px] border border-slate-200 bg-white p-6 shadow-sm space-y-4">
            <h3 className="text-xl font-black text-slate-950 mb-2">Add New Question</h3>

            <div>
              <label className="block text-xs font-black uppercase tracking-wide text-slate-500 mb-1">Question Statement</label>
              <textarea
                value={form.questionText}
                onChange={(e) => setForm({ ...form, questionText: e.target.value })}
                rows={3}
                placeholder="Enter question text..."
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 p-3 text-sm font-bold outline-none"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <input value={form.optionA} onChange={(e) => setForm({ ...form, optionA: e.target.value })} placeholder="Option A" className="h-11 rounded-xl border border-slate-200 bg-slate-50 px-3 text-xs font-bold outline-none" required />
              <input value={form.optionB} onChange={(e) => setForm({ ...form, optionB: e.target.value })} placeholder="Option B" className="h-11 rounded-xl border border-slate-200 bg-slate-50 px-3 text-xs font-bold outline-none" required />
              <input value={form.optionC} onChange={(e) => setForm({ ...form, optionC: e.target.value })} placeholder="Option C" className="h-11 rounded-xl border border-slate-200 bg-slate-50 px-3 text-xs font-bold outline-none" required />
              <input value={form.optionD} onChange={(e) => setForm({ ...form, optionD: e.target.value })} placeholder="Option D" className="h-11 rounded-xl border border-slate-200 bg-slate-50 px-3 text-xs font-bold outline-none" required />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-black uppercase tracking-wide text-slate-500 mb-1">Correct Answer</label>
                <select value={form.correctOption} onChange={(e) => setForm({ ...form, correctOption: e.target.value })} className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-xs font-bold">
                  <option value="A">Option A</option>
                  <option value="B">Option B</option>
                  <option value="C">Option C</option>
                  <option value="D">Option D</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-black uppercase tracking-wide text-slate-500 mb-1">Explanation</label>
                <input value={form.explanation} onChange={(e) => setForm({ ...form, explanation: e.target.value })} placeholder="Optional" className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-xs font-bold outline-none" />
              </div>
            </div>

            <button type="submit" className="w-full rounded-2xl bg-blue-600 px-5 py-3.5 text-xs font-black text-white hover:bg-blue-700 shadow-lg shadow-blue-600/20 transition">
              + Add Question Manually 📝
            </button>
          </form>

          {/* BULK CSV UPLOAD FORM */}
          <form onSubmit={handleBulkUpload} className="rounded-[30px] border border-blue-100 bg-blue-50/40 p-6 shadow-sm space-y-3">
            <h3 className="text-base font-black text-blue-900">Bulk Upload (CSV)</h3>
            <p className="text-[11px] font-bold text-slate-500">
              Columns required: Question, OptA, OptB, OptC, OptD, Correct(A/B/C/D), Explanation
            </p>
            <input
              type="file"
              accept=".csv"
              onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
              className="w-full text-xs text-slate-500 file:mr-3 file:py-2 file:px-3 file:rounded-xl file:border-0 file:text-xs file:font-black file:bg-blue-600 file:text-white"
              required
            />
            <button type="submit" disabled={uploading} className="w-full py-3 bg-blue-600 text-white text-xs font-black rounded-xl hover:bg-blue-700 disabled:bg-slate-300 shadow-md">
              {uploading ? "Importing..." : "Upload CSV & Import Bulk 📂"}
            </button>
          </form>
        </div>

       {/* RIGHT COLUMN: QUESTIONS LIST */}
        <div className="rounded-[30px] border border-slate-200 bg-white p-6 shadow-sm h-fit">
          <h3 className="text-xl font-black text-slate-950 mb-5">Questions in Selected Test</h3>

          {loading ? (
            <div className="py-20 text-center text-sm font-bold text-slate-400">Loading questions...</div>
          ) : (
            <div className="space-y-4">
              {questions.map((q, idx) => (
                <div key={q.id} className="rounded-2xl border border-slate-100 bg-[#f8fbff] p-5 relative">
                  <div className="flex justify-between items-center mb-3">
                    <span className="rounded-full bg-blue-100 px-3 py-0.5 text-[10px] font-black text-blue-700">Question #{idx + 1}</span>
                    <button
                      onClick={() => handleDelete(q.id)}
                      className="text-xs font-black text-rose-600 bg-rose-50 hover:bg-rose-100 px-2.5 py-1 rounded-lg transition"
                    >
                      Delete 🗑️
                    </button>
                  </div>
                  
                  <p className="text-sm font-black text-slate-900 whitespace-pre-line">{q.question}</p>
                  
                  {/* OPTIONS UI: 4 alag boxes me options dikhane ke liye */}
                  <div className="mt-4 grid grid-cols-2 gap-2 text-xs font-bold text-slate-600">
                    <div className={`p-2.5 rounded-xl border ${q.correct_option === 'A' ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : 'bg-white border-slate-200'}`}>
                      <span className="font-black mr-1">A)</span> {q.option_a}
                    </div>
                    <div className={`p-2.5 rounded-xl border ${q.correct_option === 'B' ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : 'bg-white border-slate-200'}`}>
                      <span className="font-black mr-1">B)</span> {q.option_b}
                    </div>
                    <div className={`p-2.5 rounded-xl border ${q.correct_option === 'C' ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : 'bg-white border-slate-200'}`}>
                      <span className="font-black mr-1">C)</span> {q.option_c}
                    </div>
                    <div className={`p-2.5 rounded-xl border ${q.correct_option === 'D' ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : 'bg-white border-slate-200'}`}>
                      <span className="font-black mr-1">D)</span> {q.option_d}
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-slate-200/60 flex flex-col gap-1.5 text-xs font-bold text-slate-500">
                    <span>Correct Answer: <strong className="text-emerald-700">Option {q.correct_option}</strong></span>
                    {q.explanation && <span>Explanation: <span className="text-slate-700">{q.explanation}</span></span>}
                  </div>
                </div>
              ))}

              {questions.length === 0 && (
                <div className="py-24 text-center rounded-2xl border border-dashed border-slate-200 bg-slate-50">
                  <span className="text-4xl mb-2 block">📝</span>
                  <p className="text-sm font-bold text-slate-400">No questions found for this test. Add some manually or via CSV!</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}